import React from 'react';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { Button, Form, Input, Label } from '../../../components/Bootstrap';
import { NobleModal } from '../../../components';
import {
  Menu_menus as Menu,
  Menu_menu as CurrentMenu,
  Menu_menu_categories_items as MenuItem,
} from './__generated__/Menu';
import { CopyMenuItemQuery, CopyMenuItemQueryVariables } from './__generated__/CopyMenuItemQuery';

interface CopyMenuItemModalProps {
  venueId: string;
  toggle: () => void;
  menus: Menu[];
  menuItem: MenuItem;
  currentMenu: CurrentMenu;
  copyMenuItem(data: { toMenuId: string; toMenuCategoryId: string });
}

const SELECT_OPTION = '----';

const COPY_MENU_ITEM_QUERY = gql`
  query CopyMenuItemQuery($venueId: ID!, $menuId: ID!) {
    menu(venueId: $venueId, menuId: $menuId) {
      id
      categories {
        id
        name
      }
    }
  }
`;

export const CopyMenuItemModal: React.FC<CopyMenuItemModalProps> = (props) => {
  const { venueId, copyMenuItem, currentMenu, menus, toggle, menuItem } = props;
  const [toMenuId, setToMenuId] = React.useState(SELECT_OPTION);
  const [toMenuCategoryId, setToMenuCategoryId] = React.useState(SELECT_OPTION);

  const { loading, data } = useQuery<CopyMenuItemQuery, CopyMenuItemQueryVariables>(COPY_MENU_ITEM_QUERY, {
    skip: toMenuId === SELECT_OPTION,
    variables: {
      venueId,
      menuId: toMenuId,
    },
  });

  const onSubmit = React.useCallback(
    (e) => {
      e.preventDefault();

      copyMenuItem({
        toMenuId,
        toMenuCategoryId,
      });
    },
    [toMenuId, toMenuCategoryId, copyMenuItem]
  );

  const onChangeToMenu = React.useCallback(
    (e) => {
      setToMenuId(e.target.value);
      setToMenuCategoryId(SELECT_OPTION);
    },
    [setToMenuId, setToMenuCategoryId]
  );

  const onChangeToMenuCategoryId = React.useCallback(
    (e) => {
      setToMenuCategoryId(e.target.value);
    },
    [setToMenuCategoryId]
  );

  const toMenues = menus.filter((menu) => {
    return menu.id !== currentMenu.id;
  });

  const canSubmit = toMenuId !== SELECT_OPTION && toMenuCategoryId !== SELECT_OPTION;

  return (
    <NobleModal size='lg' isOpen={true} toggle={toggle}>
      <NobleModal.Title title='Copy Menu Item' />
      <NobleModal.Body>
        <p className='menu-modal-desc text-center'>Copy {menuItem.item.name} to another menu.</p>
        <Form onSubmit={onSubmit} className='menu-modal-input'>
          <div className='mt-5 mb-5'>
            <Label className='text-right' for='option'>
              From:
            </Label>
            <p>{currentMenu.name}</p>
          </div>
          <div className='mt-5 mb-5'>
            <Label className='text-right' for='option'>
              To:
            </Label>
            <Input type='select' value={toMenuId} onChange={onChangeToMenu} required={true}>
              <option value={SELECT_OPTION}>{SELECT_OPTION}</option>
              {toMenues.map((menu) => (
                <option key={menu.id} value={menu.id}>
                  {menu.name}
                </option>
              ))}
            </Input>
          </div>
          <div className='mt-5 mb-5'>
            <Label className='text-right' for='option'>
              To Category:
            </Label>
            {loading && <p>Loading categories...</p>}
            {data && (
              <Input type='select' value={toMenuCategoryId} onChange={onChangeToMenuCategoryId} required={true}>
                <option value={SELECT_OPTION}>{SELECT_OPTION}</option>
                {data.menu.categories
                  .filter((category) => Number(category.id) > 0)
                  .map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
              </Input>
            )}
          </div>
          <div className='text-center menu-modal-btns'>
            <Button type='button' color='link' className='me-4' onClick={toggle}>
              Cancel
            </Button>{' '}
            <Button disabled={!canSubmit} type='submit' color='primary'>
              Copy
            </Button>
          </div>
        </Form>
      </NobleModal.Body>
    </NobleModal>
  );
};
